import '../styles/global.scss'
import { DefaultSeo } from 'next-seo'
import { SEO } from '../constants/index'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { LinkProvider } from '@fanduel/og-fe-library'
import Link from 'next/link'
import { PerimeterXScript } from '@/components/Misc/PerimeterXScript'
import { AmplitudeProvider } from '@/utils/hooks/useAmplitude'
import Script from 'next/script'
import GlobalStyles from 'GlobalStyles'
import { useRouter } from 'next/router'
import Rum from '@/components/Misc/Rum'

declare global {
  interface Window {
    amplitude: unknown
  }
}

export default function MyApp({ Component, pageProps }: Readonly<{ Component: any; pageProps: any }>) {
  const NEXT_PUBLIC_VISIBLE_TO_SEARCH_ENGINES = process.env.NEXT_PUBLIC_VISIBLE_TO_SEARCH_ENGINES
  const titleTemplate = `%s | ${SEO.SITE_NAME}`

  const router = useRouter()
  const currentPath = router.pathname
  const currentUrl = router.asPath

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 100000,
          },
        },
      }),
  )

  // Define the Rum configuration based on environment variables
  const rumConfig = {
    // Pass the raw value so the runtime BAKED_ replacement remains in the bundle.
    ddTraceEnabled: process.env.NEXT_PUBLIC_DD_TRACE_ENABLED || 'false',
    ddAppId: process.env.NEXT_PUBLIC_DD_RUM_APP_ID || '',
    ddAppToken: process.env.NEXT_PUBLIC_DD_RUM_APP_TOKEN || '',
    ddEnv: process.env.NEXT_PUBLIC_DD_ENV || 'local',
    ddService: process.env.NEXT_PUBLIC_DD_SERVICE || 'contentx-marketing-frontend',
    ddVersion: process.env.NEXT_PUBLIC_DD_VERSION || '0.0.0',
    ddSite: 'datadoghq.com',
    ddSampleRate: 5, // 5% sample rate
  }

  return (
    <>
      <Script data-testid="google-tag-manager" id="google-tag-manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-5DGV8G4M');`}
      </Script>
      <QueryClientProvider client={queryClient}>
        <Rum config={rumConfig} />
        <AmplitudeProvider>
          <LinkProvider CustomLink={Link}>
            <PerimeterXScript />
            <main>
              <div style={{ zIndex: 0, display: 'none' }} data-testid="data-dog">
                <span suppressHydrationWarning> {`URL: ${currentUrl}`}</span>
              </div>
              <GlobalStyles>
                <DefaultSeo
                  titleTemplate={titleTemplate}
                  defaultTitle="Daily Fantasy Sports and Online U.S. Sportsbook | FanDuel"
                  dangerouslySetAllPagesToNoIndex={NEXT_PUBLIC_VISIBLE_TO_SEARCH_ENGINES?.toLowerCase() !== 'true'}
                  dangerouslySetAllPagesToNoFollow={NEXT_PUBLIC_VISIBLE_TO_SEARCH_ENGINES?.toLowerCase() !== 'true'}
                  openGraph={{
                    type: 'website',
                    locale: 'en_US',
                    url: SEO.FANDUEL_URL,
                    siteName: SEO.SITE_NAME,
                  }}
                  twitter={{
                    handle: SEO.TWITTERHANDLE,
                    site: SEO.SITE_NAME,
                    cardType: 'summary_large_image',
                  }}
                />
                <Component {...pageProps} />
              </GlobalStyles>
            </main>
          </LinkProvider>
        </AmplitudeProvider>
      </QueryClientProvider>
    </>
  )
}
